import type { PlayersColumnLayout } from "../features/dashboard/sections/players/playersTableColumns";
import { EMPTY_COLUMN_LAYOUT } from "../features/dashboard/sections/players/playersTableColumns";

const STORAGE_KEY_PREFIX = "playersColumns";

/** localStorage key for the players-table column layout of a specific organization. */
const getStorageKey = (organizationId: string): string => {
  return `${STORAGE_KEY_PREFIX}_${organizationId}`;
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === "string");

/**
 * A stored layout is only usable if both halves are string arrays. Anything else
 * is treated as absent rather than partially applied — half a layout would
 * arrange columns while ignoring which are hidden, which looks like the feature
 * malfunctioning rather than like nothing was saved.
 */
const isColumnLayout = (value: unknown): value is PlayersColumnLayout => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return isStringArray(candidate.order) && isStringArray(candidate.hidden);
};

/**
 * Read the persisted column layout for an organization. Falls back to the empty
 * layout — which `buildPlayersColumns` renders as the default column set — when
 * there is no org, nothing stored, or a value that isn't a layout.
 *
 * Unknown keys inside a valid layout are *not* filtered here; `buildPlayersColumns`
 * resolves them against the columns that currently exist, because only it knows
 * the organization's tag categories.
 */
export const getStoredColumnLayout = (
  organizationId: string | null
): PlayersColumnLayout => {
  if (!organizationId) {
    return EMPTY_COLUMN_LAYOUT;
  }

  try {
    const stored = localStorage.getItem(getStorageKey(organizationId));
    if (!stored) {
      return EMPTY_COLUMN_LAYOUT;
    }

    const parsed: unknown = JSON.parse(stored);
    if (!isColumnLayout(parsed)) {
      console.warn(
        `Invalid players column layout in localStorage for org ${organizationId}`
      );
      return EMPTY_COLUMN_LAYOUT;
    }

    return { order: parsed.order, hidden: parsed.hidden };
  } catch (error) {
    console.warn(
      "Failed to load players column layout from localStorage:",
      error
    );
    return EMPTY_COLUMN_LAYOUT;
  }
};

/** Persist the column layout for an organization. */
export const saveColumnLayout = (
  organizationId: string | null,
  layout: PlayersColumnLayout
): void => {
  if (!organizationId) {
    return;
  }

  try {
    localStorage.setItem(getStorageKey(organizationId), JSON.stringify(layout));
  } catch (error) {
    console.warn(
      "Failed to save players column layout to localStorage:",
      error
    );
  }
};

/**
 * Clear every organization's stored layout. Called on logout so one coach's
 * column arrangement never greets the next user on a shared machine.
 */
export const clearAllColumnLayouts = (): void => {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn(
      "Failed to clear all players column layouts from localStorage:",
      error
    );
  }
};
